'use client'

import { useCallback, useMemo } from 'react'
import useSWR, { type Key, type SWRConfiguration, useSWRConfig } from 'swr'
import useSWRInfinite, {
  type SWRInfiniteConfiguration,
  type SWRInfiniteKeyLoader,
} from 'swr/infinite'
import useSWRMutation from 'swr/mutation'

import { handleApiError } from '@/lib/api-error-handler'
import type { MetricRegistryResponse } from '@/components/dashboard/types'
import {
  type FetcherOptions,
  swrFetcher,
  ApiFetchError,
  buildQueryString,
  parseQueryString,
} from './api-fetcher'

// Re-export types for convenience
export type { ApiError, ApiResponse, FetcherOptions } from './api-fetcher'
export { ApiFetchError } from './api-fetcher'

interface UseApiQueryOptions<T> extends SWRConfiguration<T, Error> {
  /**
   * Query params. Array values serialize as repeated params and survive the
   * key round-trip, so multi-select facets reach `list[str]` backends.
   */
  query?: Record<string, string | string[]>
  headers?: Record<string, string>
  /** If false, the query will not execute */
  enabled?: boolean
  /**
   * Legacy SWR key bridge for code that already has cross-component mutate()
   * calls. Prefer the URL-derived key for new queries.
   */
  cacheKey?: Key
  /** Let a surface with its own error state avoid a duplicate global toast. */
  suppressGlobalErrorHandler?: boolean
}

interface UseApiMutationOptions<T> {
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  headers?: Record<string, string>
  onSuccess?: (data: T) => void
  onError?: (error: Error) => void
  suppressGlobalErrorHandler?: boolean
}

/**
 * SWR-based hook for GET requests with automatic caching and revalidation.
 *
 * @example
 * const { data, error, isLoading } = useApiQuery<User[]>('/api/users')
 *
 * @example
 * const { data } = useApiQuery<User>('/api/users/123', {
 *   query: { include: 'profile' },
 *   revalidateOnFocus: false,
 * })
 */
export function useApiQuery<T>(
  url: string | null,
  options: UseApiQueryOptions<T> = {},
) {
  const {
    query,
    headers,
    enabled = true,
    cacheKey,
    suppressGlobalErrorHandler = false,
    ...swrOptions
  } = options

  const requestKey = useMemo(() => {
    if (!url) return null
    if (!query || Object.keys(query).length === 0) return url
    const queryString = buildQueryString(query)
    return queryString ? `${url}?${queryString}` : url
  }, [url, query])

  const fetcher = useCallback(async (): Promise<T> => {
    if (!requestKey) {
      throw new Error('Missing API query URL')
    }
    // Extract base URL and query from compound key (url?query). Repeated
    // params are grouped back into arrays so multi-value facets are not
    // collapsed to their last value on the way to the fetcher.
    const [baseUrl, queryString] = requestKey.split('?')
    const keyQuery = queryString ? parseQueryString(queryString) : query
    try {
      // The bearer token is resolved inside swrFetcher from the self-auth
      // token-manager singleton — no React auth context needed here.
      return await swrFetcher<T>(baseUrl, {
        method: 'GET',
        query: keyQuery,
        headers,
      })
    } catch (error) {
      if (!suppressGlobalErrorHandler && error instanceof ApiFetchError) {
        handleApiError(error, error.url, error.statusCode)
      }
      throw error
    }
  }, [requestKey, query, headers, suppressGlobalErrorHandler])

  // Build SWR key that includes query parameters to ensure proper caching
  const swrKey = useMemo(() => {
    if (!enabled || !url) return null
    return cacheKey === undefined ? requestKey : cacheKey
  }, [cacheKey, enabled, requestKey, url])

  return useSWR<T, Error>(swrKey, fetcher, {
    revalidateOnFocus: false,
    ...swrOptions,
  })
}

interface UseApiInfiniteOptions<T> extends SWRInfiniteConfiguration<T, Error> {
  headers?: Record<string, string>
}

/**
 * SWR-infinite hook for cursor/page-accumulating GET lists (infinite scroll).
 *
 * The single sanctioned wrapper over `useSWRInfinite` — bare `useSWRInfinite`
 * to `/api/*` is a red-line violation, same as bare `useSWR`/`fetch`. `getKey`
 * returns the full request URL for each page (with the page param + serialized
 * query, e.g. via `buildQueryString`) or `null` to stop; changing the page-1
 * key (e.g. when filters change) resets the accumulated pages. Pages share the
 * same auth/trace/error handling as `useApiQuery`.
 */
export function useApiInfinite<T>(
  getKey: SWRInfiniteKeyLoader<T, string | null>,
  options: UseApiInfiniteOptions<T> = {},
) {
  const { headers, ...swrOptions } = options

  const fetcher = useCallback(
    async (key: string): Promise<T> => {
      const [baseUrl, queryString] = key.split('?')
      const keyQuery = queryString ? parseQueryString(queryString) : undefined
      try {
        return await swrFetcher<T>(baseUrl, {
          method: 'GET',
          query: keyQuery,
          headers,
        })
      } catch (error) {
        if (error instanceof ApiFetchError) {
          handleApiError(error, error.url, error.statusCode)
        }
        throw error
      }
    },
    [headers],
  )

  return useSWRInfinite<T, Error>(getKey, fetcher, {
    revalidateOnFocus: false,
    revalidateFirstPage: false,
    ...swrOptions,
  })
}

interface MutationArg<A> {
  body?: A
  query?: Record<string, string>
}

/**
 * SWR Mutation hook for POST/PUT/PATCH/DELETE requests.
 *
 * @example
 * const { trigger, isMutating } = useApiMutation<User, CreateUserInput>('/api/users')
 * await trigger({ body: { name: 'John' } })
 *
 * @example
 * const { trigger } = useApiMutation<void, { id: string }>('/api/users/123', {
 *   method: 'DELETE',
 * })
 * await trigger({ body: { id: '123' } })
 */
export function useApiMutation<T, A = undefined>(
  url: string,
  options: UseApiMutationOptions<T> = {},
) {
  const {
    method = 'POST',
    headers,
    onSuccess,
    onError,
    suppressGlobalErrorHandler = false,
  } = options

  const fetcher = useCallback(
    async (_key: string, { arg }: { arg: MutationArg<A> }): Promise<T> => {
      try {
        return await swrFetcher<T>(url, {
          method,
          body: arg.body,
          query: arg.query,
          headers,
        })
      } catch (error) {
        if (!suppressGlobalErrorHandler && error instanceof ApiFetchError) {
          handleApiError(error, error.url, error.statusCode)
        }
        throw error
      }
    },
    [url, method, headers, suppressGlobalErrorHandler],
  )

  return useSWRMutation<T, Error, string, MutationArg<A>>(url, fetcher, {
    onSuccess,
    onError,
  })
}

/**
 * Hook that provides a simple request function for one-off API calls.
 * Use this for imperative calls that don't need SWR caching.
 *
 * @example
 * const { request } = useApiRequest()
 * const result = await request<User>({ url: '/api/users/123' })
 */
export function useApiRequest(
  options: { suppressGlobalErrorHandler?: boolean } = {},
) {
  const { suppressGlobalErrorHandler = false } = options
  const request = useCallback(
    async <T = unknown>(
      options: FetcherOptions & { url: string },
    ): Promise<T> => {
      const { url, ...rest } = options
      try {
        return await swrFetcher<T>(url, rest)
      } catch (error) {
        if (!suppressGlobalErrorHandler && error instanceof ApiFetchError) {
          handleApiError(error, error.url, error.statusCode)
        }
        throw error
      }
    },
    [suppressGlobalErrorHandler],
  )

  return { request }
}

/** Central cache invalidation entry for cross-component API query keys. */
export function useApiCache() {
  return useSWRConfig()
}

export function useMetricRegistry(source: 'ads' | 'ga4' = 'ads') {
  return useApiQuery<MetricRegistryResponse>('/api/dashboard/metrics', {
    query: { source },
    revalidateOnFocus: false,
  })
}
